import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from '@sentry/react';

import Loading from 'components/Loading';
import ErrorBoundayFallback from 'components/ErrorBoundayFallback';

import { MetaTagsProvider } from 'utils/metatags';

import WebConsolePage from './webConsole';
import ProfilePage from './profile';
import BillingPage from './billing';
import UpgradePage from './upgrade';
import CheckoutCompletePage from './checkoutComplete';
import ProjectsPage from './projects';
import ProjectPage from './project';
import SharePage from './share';
import TeamPage from './team';
import IntegrationPage from './integration';

const ErrorPage = lazy(() => import('./404'));
const LoginPage = lazy(() => import('./login'));
const ForgotPasswordPage = lazy(() => import('./forgotPassword'));
const UserAction = lazy(() => import('modules/Users/container/UserAction'));
const SignupPage = lazy(() => import('./signup'));

const Pages = () => {
  const renderContent = () => (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/user-action" element={<UserAction />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route
          path="/projects/archived"
          element={<ProjectsPage matchpath="/projects/archived" />}
        />
        <Route
          path="/projects/web"
          element={<ProjectsPage matchpath="/projects/web" />}
        />
        <Route
          path="/projects/illustration"
          element={<ProjectsPage matchpath="/projects/illustration" />}
        />
        <Route
          path="/projects"
          element={<ProjectsPage matchpath="/projects" />}
        />
        <Route path="/team" element={<TeamPage matchpath="/team" />} />
        <Route
          path="/team/:teamID"
          element={<TeamPage matchpath="/team/:teamID" />}
        />
        <Route path="/profile" element={<ProfilePage matchpath="/profile" />} />
        <Route path="/billing" element={<BillingPage matchpath="/billing" />} />
        <Route path="/upgrade" element={<UpgradePage matchpath="/upgrade" />} />
        <Route
          path="/upgrade/confirm/:plan"
          element={<UpgradePage matchpath="/upgrade/confirm/:plan" />}
        />
        <Route
          path="/profile/checkout-complete"
          element={
            <CheckoutCompletePage matchpath="/profile/checkout-complete" />
          }
        />
        <Route
          path="/project/:projectID/:pageID/:versionID/:currentView"
          element={
            <WebConsolePage matchpath="/project/:projectID/:pageID/:versionID/:currentView" />
          }
        />
        <Route
          path="/project/:projectID/:pageID/:versionID"
          element={
            <WebConsolePage matchpath="/project/:projectID/:pageID/:versionID/:currentView" />
          }
        />
        <Route
          path="/project/:projectID/archived"
          element={<ProjectPage matchpath="/project/:projectID/archived" />}
        />
        <Route
          path="/project/:projectID/:pageID"
          element={<WebConsolePage matchpath="/project/:projectID/:pageID" />}
        />
        <Route
          path="/project/:projectID"
          element={<ProjectPage matchpath="/project/:projectID" />}
        />
        <Route
          path="/share/:projectID"
          element={<SharePage matchpath="/share/:projectID" />}
        />
        <Route
          path="/preview/:projectID/:pageID/:versionID"
          element={
            <SharePage matchpath="/preview/:projectID/:pageID/:versionID" />
          }
        />
        <Route path="/integration/:app" element={<IntegrationPage />} />
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </Suspense>
  );

  if (process.env.REACT_APP_ENV === 'production') {
    return (
      <ErrorBoundary fallback={<ErrorBoundayFallback />}>
        {renderContent()}
      </ErrorBoundary>
    );
  }

  return <MetaTagsProvider>{renderContent()}</MetaTagsProvider>;
};

export default Pages;
